import { useEffect, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import type { Env } from '../types';
import { getAllDocs } from '../content/docs';

type ReleasesTab = Env | 'site';

interface ReleaseWorkflow {
  name: string;
  version: string;
  kind: string;
  changes?: string[];
}

interface ReleaseEntry {
  buildId: string;
  deployedAt: string;
  branch: string;
  env: string;
  pipelineName?: string;
  pipelineUrl: string;
  workflows: ReleaseWorkflow[];
  // Optional structured change-request context emitted by the deploy pipeline
  // (Phase 2). When present it enriches the auto QA table without curation.
  changeRef?: string;
  changeSummary?: string;
}

interface SiteReleaseEntry {
  buildId: string;
  deployedAt: string;
  branch: string;
  pipelineName?: string;
  pipelineUrl: string;
  changes: string[];
  changeRef?: string;
  changeSummary?: string;
}

// A single curated QA change row (optional override of the auto row-set for a
// build). Feeds the QA table columns: Task / Description / Date / Deliverable.
interface CuratedChange {
  task?: string; // change-request reference, e.g. "CR-ERR-001"
  taskUrl?: string; // optional explicit link for the task
  pr?: string | number; // ADO pull-request number (auto-linked if taskUrl absent)
  description: string;
  date?: string; // per-row date; defaults to the release deployedAt
  deliverable?: string; // free-text deliverable label; defaults to the workflow
  deliverableName?: string; // workflow/API name to deep-link to its docs page
}

// Curated, audience-facing notes for a specific build (optional override of the
// auto-derived notes). Human-authored + PR-reviewed, committed at
// public/release-notes.json and served as an array keyed by buildId. Any build
// without an entry falls back to auto-derived highlights + an auto QA table.
interface CuratedRelease {
  buildId: string;
  title?: string;
  highlights?: string[];
  // QA release notes: when present, these rows populate the Task/Description/
  // Date/Deliverable table instead of the auto-derived per-workflow rows.
  changes?: CuratedChange[];
}

// A rendered QA table row (from curated data or auto-derived).
interface QaRow {
  task?: string;
  taskUrl?: string;
  description: string;
  date: string;
  deliverableLabel?: string;
  docSlug?: string;
}

// Map a workflow/API name to its documentation slug (…/workflows/<group>/<name>)
// resolved from the actual docs registry, so Deliverable links never 404. Built
// once at module load (the docs glob is eager).
const WORKFLOW_DOC_SLUG: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const d of getAllDocs()) {
    const m = d.slug.match(/(?:^|\/)workflows\/[^/]+\/([^/]+)$/);
    if (m && !map[m[1]]) map[m[1]] = d.slug;
  }
  return map;
})();

const ADO_GIT_BASE = 'https://dev.azure.com/cipdvlz/Claude%20Code%20Checking/_git';

function repoForTab(tab: ReleasesTab): string {
  return tab === 'site' ? 'synergy-platform-site' : 'synergy-application-repo';
}

// Pull an ADO pull-request number out of a commit subject
// ("Merge pull request 1743 …", "… (#1743)", "PR 1743").
function parsePr(subjects: string[]): string | undefined {
  for (const s of subjects) {
    const m =
      s.match(/pull request\s+(\d+)/i) || s.match(/\(#(\d+)\)/) || s.match(/\bPR\s*#?(\d+)\b/i);
    if (m) return m[1];
  }
  return undefined;
}

function prUrl(pr: string | number, tab: ReleasesTab): string {
  return `${ADO_GIT_BASE}/${repoForTab(tab)}/pullrequest/${pr}`;
}

// Build the QA rows for a release: curated rows if supplied, else one auto row
// per changed workflow (Task = PR, Description = cleaned change text,
// Date = deploy date, Deliverable = the workflow linked to its docs).
function qaRows(
  deployedAt: string,
  workflows: ReleaseWorkflow[] | undefined,
  siteChanges: string[] | undefined,
  curatedEntry: CuratedRelease | undefined,
  tab: ReleasesTab,
  changeRef?: string,
  changeSummary?: string,
): QaRow[] {
  if (curatedEntry?.changes?.length) {
    return curatedEntry.changes.map((c) => {
      const docSlug = c.deliverableName ? WORKFLOW_DOC_SLUG[c.deliverableName] : undefined;
      return {
        task: c.task ?? (c.pr != null ? `PR ${c.pr}` : undefined),
        taskUrl: c.taskUrl ?? (c.pr != null ? prUrl(c.pr, tab) : undefined),
        description: c.description,
        date: c.date ?? deployedAt,
        deliverableLabel: c.deliverable ?? c.deliverableName,
        docSlug,
      };
    });
  }
  // Auto baseline: combine the deploy's change-request reference (from the
  // pipeline) with the per-workflow PR into the Task, and fall back to the CR
  // summary when a workflow has no commit text of its own.
  const cr = (changeRef ?? '').trim();
  const composeTask = (pr?: string): string | undefined => {
    const parts: string[] = [];
    if (cr) parts.push(cr);
    if (pr) parts.push(`PR ${pr}`);
    return parts.length ? parts.join(' · ') : undefined;
  };
  if (workflows && workflows.length) {
    return workflows.map((w) => {
      const pr = parsePr(w.changes ?? []);
      const desc = autoHighlights(w.changes ?? []).join('; ');
      return {
        task: composeTask(pr),
        taskUrl: pr ? prUrl(pr, tab) : undefined,
        description: desc || changeSummary || 'Deployed (no change detail recorded).',
        date: deployedAt,
        deliverableLabel: `${w.name} v${w.version}`,
        docSlug: WORKFLOW_DOC_SLUG[w.name],
      };
    });
  }
  if (siteChanges && siteChanges.length) {
    return siteChanges.map((c) => {
      const pr = parsePr([c]);
      return {
        task: composeTask(pr),
        taskUrl: pr ? prUrl(pr, tab) : undefined,
        description: toHighlight(c),
        date: deployedAt,
        deliverableLabel: 'Site',
      };
    });
  }
  return [];
}

// The QA release-notes table: Task / Description / Date / Deliverable.
function QaTable({ rows }: { rows: QaRow[] }) {
  if (!rows.length) return null;
  return (
    <table className="release-table release-qa-table">
      <thead>
        <tr>
          <th>Task</th>
          <th>Description</th>
          <th>Date</th>
          <th>Deliverable</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            <td>
              {row.taskUrl ? (
                <a href={row.taskUrl} target="_blank" rel="noreferrer">
                  {row.task}
                </a>
              ) : (
                row.task ?? '—'
              )}
            </td>
            <td>{row.description}</td>
            <td>
              {new Date(row.date).toLocaleDateString(undefined, { dateStyle: 'medium' })}
            </td>
            <td>
              {row.docSlug ? (
                <Link to={`/docs/${row.docSlug}`}>{row.deliverableLabel}</Link>
              ) : (
                row.deliverableLabel ?? '—'
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Turn a raw git commit subject into a reader-facing highlight: drop a leading
// conventional/scope prefix (e.g. "APIM:", "syn-contact-merge:", "feat(x):") and
// capitalise the first letter. Otherwise leaves the sentence intact.
function toHighlight(subject: string): string {
  const s = subject.trim();
  const m = s.match(/^([A-Za-z0-9 _\-/().]{1,40}):\s+(\S.*)$/);
  const text = (m ? m[2] : s).trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

// De-duplicated functional highlights derived from a set of commit subjects.
function autoHighlights(subjects: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of subjects) {
    const h = toHighlight(raw);
    const key = h.toLowerCase();
    if (h && !seen.has(key)) {
      seen.add(key);
      out.push(h);
    }
  }
  return out;
}

const TABS: ReleasesTab[] = ['test', 'dev', 'uat', 'prod', 'site'];

function isValidTab(s: string | undefined): s is ReleasesTab {
  return s === 'test' || s === 'dev' || s === 'uat' || s === 'prod' || s === 'site';
}

export default function ReleasesPage() {
  const { env: envParam } = useParams();
  const tab: ReleasesTab = isValidTab(envParam) ? envParam : 'test';
  const isSite = tab === 'site';

  const [releases, setReleases] = useState<ReleaseEntry[] | null>(null);
  const [siteReleases, setSiteReleases] = useState<SiteReleaseEntry[] | null>(null);
  const [curated, setCurated] = useState<Record<string, CuratedRelease>>({});
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'missing'>('loading');

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setReleases(null);
    setSiteReleases(null);
    setCurated({});

    // Optional curated functional notes — best-effort; the page works without them.
    fetch('release-notes.json', { cache: 'no-cache' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: CuratedRelease[]) => {
        if (cancelled || !Array.isArray(data)) return;
        const map: Record<string, CuratedRelease> = {};
        for (const c of data) if (c && c.buildId) map[String(c.buildId)] = c;
        setCurated(map);
      })
      .catch(() => {
        /* curated notes are optional */
      });

    const url = isSite
      ? 'platform-content/synergy-application/site/releases.json'
      : `platform-content/synergy-application/${tab}/releases.json`;

    fetch(url, { cache: 'no-cache' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          if (isSite) {
            setSiteReleases(Array.isArray(data) ? (data as SiteReleaseEntry[]) : []);
          } else {
            setReleases(Array.isArray(data) ? (data as ReleaseEntry[]) : []);
          }
          setLoadState('loaded');
        }
      })
      .catch(() => {
        if (!cancelled) setLoadState('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [tab, isSite]);

  const envLabel = isSite ? 'SITE' : tab.toUpperCase();

  return (
    <div className="page section-page">
      <div className="section-heading">
        <h2>Releases</h2>
      </div>
      <p>
        What changed in each release. Each entry lists its changes in a Task / Description / Date /
        Deliverable table, where each Deliverable links to the documentation for the API or workflow
        that changed, and each Task links to its pull request. The full technical deployment detail
        (workflows, versions, build) is a click away under each entry. Workflow releases come from{' '}
        <em>synergy-application-stage</em>; site releases come from{' '}
        <em>synergy-platform-site-deploy</em>.
      </p>

      <nav className="section-envnav" aria-label="releases environments">
        <div className="section-envnav-inner">
          {TABS.map((t) => (
            <NavLink
              key={t}
              to={`/releases/${t}`}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              {t.toUpperCase()}
            </NavLink>
          ))}
        </div>
      </nav>

      {loadState === 'loading' && <p className="tile-loading">Loading…</p>}

      {loadState === 'missing' && (
        <div className="section-placeholder">
          <p>No releases have been published yet for <strong>{envLabel}</strong>.</p>
          <p className="note">
            {isSite
              ? 'Site releases are published by synergy-platform-site-deploy. They appear here after the next site deployment.'
              : `Releases are published by synergy-application-stage. They appear here after the next deployment to this estate.`}
          </p>
        </div>
      )}

      {loadState === 'loaded' && !isSite && releases && releases.length === 0 && (
        <div className="section-placeholder">
          <p>No releases recorded yet for <strong>{envLabel}</strong>.</p>
        </div>
      )}

      {loadState === 'loaded' && isSite && siteReleases && siteReleases.length === 0 && (
        <div className="section-placeholder">
          <p>No site releases recorded yet.</p>
        </div>
      )}

      {loadState === 'loaded' && !isSite && releases && releases.length > 0 && (
        <div className="releases-list">
          {releases.map((r, i) => {
            const highlights =
              curated[r.buildId]?.highlights ??
              autoHighlights(r.workflows.flatMap((w) => w.changes ?? []));
            const rows = qaRows(
              r.deployedAt,
              r.workflows,
              undefined,
              curated[r.buildId],
              tab,
              r.changeRef,
              r.changeSummary,
            );
            return (
            <details key={r.buildId} className="release-entry" open={i === 0}>
              <summary className="release-summary">
                <span className="release-date">
                  {new Date(r.deployedAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
                <span className="release-badge">{tab.toUpperCase()}</span>
                <span className="release-count">
                  {r.workflows.length} workflow{r.workflows.length !== 1 ? 's' : ''}
                </span>
                <a
                  href={r.pipelineUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="release-build-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.pipelineName ? `${r.pipelineName} #${r.buildId}` : `Build ${r.buildId}`}
                </a>
              </summary>
              <div className="release-body">
                {highlights.length > 0 && (
                  <div className="release-highlights">
                    {curated[r.buildId]?.title && (
                      <p className="release-highlights-title">{curated[r.buildId]!.title}</p>
                    )}
                    <ul>
                      {highlights.map((h, hi) => (
                        <li key={hi}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {rows.length > 0 && (
                  <div className="release-qa">
                    <p className="release-qa-title">Changes in this release</p>
                    <QaTable rows={rows} />
                  </div>
                )}
                {r.workflows.length === 0 ? (
                  <p className="note">No workflows were included in this deploy.</p>
                ) : (
                  <details className="release-tech">
                    <summary>
                      Deployment details — {r.workflows.length} workflow
                      {r.workflows.length !== 1 ? 's' : ''}
                    </summary>
                  <table className="release-table">
                    <thead>
                      <tr>
                        <th>Workflow</th>
                        <th>Version</th>
                        <th>Kind</th>
                        <th>Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.workflows.map((w) => (
                        <tr key={w.name}>
                          <td><code>{w.name}</code></td>
                          <td>{w.version}</td>
                          <td>{w.kind}</td>
                          <td>
                            {w.changes && w.changes.length > 0 ? (
                              <ul className="release-changes">
                                {w.changes.map((c, ci) => (
                                  <li key={ci}>{c}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="release-no-changes">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </details>
                )}
              </div>
            </details>
            );
          })}
        </div>
      )}

      {loadState === 'loaded' && isSite && siteReleases && siteReleases.length > 0 && (
        <div className="releases-list">
          {siteReleases.map((r, i) => {
            const highlights = curated[r.buildId]?.highlights ?? autoHighlights(r.changes);
            const rows = qaRows(
              r.deployedAt,
              undefined,
              r.changes,
              curated[r.buildId],
              'site',
              r.changeRef,
              r.changeSummary,
            );
            return (
            <details key={r.buildId} className="release-entry" open={i === 0}>
              <summary className="release-summary">
                <span className="release-date">
                  {new Date(r.deployedAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
                <span className="release-badge">SITE</span>
                <span className="release-count">
                  {r.changes.length} change{r.changes.length !== 1 ? 's' : ''}
                </span>
                <a
                  href={r.pipelineUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="release-build-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.pipelineName ? `${r.pipelineName} #${r.buildId}` : `Build ${r.buildId}`}
                </a>
              </summary>
              <div className="release-body">
                {highlights.length > 0 && (
                  <div className="release-highlights">
                    {curated[r.buildId]?.title && (
                      <p className="release-highlights-title">{curated[r.buildId]!.title}</p>
                    )}
                    <ul>
                      {highlights.map((h, hi) => (
                        <li key={hi}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {rows.length > 0 && (
                  <div className="release-qa">
                    <p className="release-qa-title">Changes in this release</p>
                    <QaTable rows={rows} />
                  </div>
                )}
                {r.changes.length === 0 ? (
                  <p className="note">No change detail recorded for this deploy.</p>
                ) : (
                  <details className="release-tech">
                    <summary>Deployment details</summary>
                    <ul className="release-changes release-changes--site">
                      {r.changes.map((c, ci) => (
                        <li key={ci}>{c}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </details>
            );
          })}
        </div>
      )}
    </div>
  );
}
